<template>
  <div class="funnel-table-container">
    <!-- Modern Filter & Control Panel -->
    <div class="filters-panel">
      <div class="panel-row primary-row">
        <div class="branding">
          <h2>Funnel Analyse</h2>
          <p class="subtitle">Manager Dashboard</p>
        </div>
        <div class="mode-switch" role="tablist" aria-label="Modus wählen">
          <button :aria-selected="mode==='datenbasiert'" role="tab" class="segment" :class="{active: mode==='datenbasiert'}" @click="onDatenbasiertClick">
            <span class="segment-label">Datenbasiert</span>
          </button>
          <button :aria-selected="mode==='zeitbasiert'" role="tab" class="segment" :class="{active: mode==='zeitbasiert'}" @click="onZeitbasiertClick">
            <span class="segment-label">Zeitraum</span>
          </button>
        </div>
        <div class="panel-actions">
          <button class="btn ghost" @click="resetFilters" title="Filter zurücksetzen">Zurücksetzen</button>
          <button class="btn primary" @click="applyFilters">Anwenden</button>
          <button class="btn outline" @click="reload" title="Neu laden (Backend)" aria-label="Neu laden">↻</button>
              <button class="btn ghost" @click="showDebug=!showDebug" title="Debug umschalten" aria-label="Debug anzeigen">{{ showDebug? 'Debug ▼':'Debug ▲' }}</button>
        </div>
      </div>
      <div class="panel-row filters-row">
        <div v-for="f in filters" :key="f.key" class="filter-item">
          <label :for="f.key" class="filter-label">{{ f.label }}</label>
          <!-- Using vue-multiselect for modern searchable dropdown (single select mode) -->
          <Multiselect
            v-model="f.value"
            :options="f.options"
            track-by="value"
            label="label"
            :searchable="true"
            :allow-empty="true"
            :clear-on-select="true"
            :close-on-select="true"
            :show-labels="false"
            :preserve-search="true"
            placeholder="Alle"
            class="filter-select"
          />
        </div>
        <div v-if="mode==='zeitbasiert'" class="filter-item wide date-range-group">
          <label class="filter-label">Zeitraum</label>
          <div class="date-range">
            <input type="date" v-model="dateStart" :max="dateEnd || undefined" />
            <span class="date-separator">–</span>
            <input type="date" v-model="dateEnd" :min="dateStart || undefined" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDebug" class="debug-box">
      <details open>
        <summary>Debug Payload/Result</summary>
        <pre class="debug-pre">Kampagne IDs: company={{ resolvedIds.idCompany }} campaign={{ resolvedIds.idCampaign }} user={{ userStore.id }}
Modus: {{ mode }}
Letzter Nested Payload:\n{{ JSON.stringify(lastPayloadNested,null,2) }}\nLetzter Flat Payload:\n{{ JSON.stringify(lastPayloadFlat,null,2) }}\nResultaten Signatur: {{ lastResultSignature }}\nRows: {{ rows.length }}</pre>
      </details>
    </div>
    <div v-if="error" class="error-banner" role="alert">{{ error }}</div>
    <div v-if="loading" class="loading-overlay container fade-in" role="status" aria-live="polite" aria-busy="true">
      <div class="loader fullPage" id="loader" aria-hidden="true"></div>
      <div class="loadingDescription">Daten werden geladen…</div>
    </div>
    <div v-else class="funnel-table-scale-wrapper" ref="scaleWrapperRef">
      <div class="funnel-table-responsive">
        <table ref="tableRef" class="funnel-table"
          :style="{ transform: 'scale(' + tableScale + ')', width: tableWidth + 'px' }">
          <thead>
            <tr class="main-header-row">
              <th v-for="(group, gi) in columnGroups" :key="gi" :colspan="group.columns.length" class="group-header"
                :style="{ background: group.color }">
                <span v-html="group.label || '&nbsp;'"></span>
              </th>
            </tr>
            <tr class="subheader-row-1">
              <th v-for="col in flatColumns" :key="col.id" :class="[col.class, 'sortable-header', sortState.col===col.id ? 'sorted-col' : '']" :style="getHeaderStyle(col)"
                  tabindex="0"
                  :aria-sort="sortState.col === col.id ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'"
                  @click="onHeaderClick(col.id)"
                  @keydown.enter.prevent="onHeaderClick(col.id)"
                  @keydown.space.prevent="onHeaderClick(col.id)">
                <span v-html="breakHeader(col.label)"></span>
                <button v-if="col.type !== 'text'" class="sort-btn" @click.stop="onHeaderClick(col.id)" :aria-label="'Sortiere '+col.label">↕</button>
                <span v-if="sortState.col === col.id" class="sort-indicator" aria-hidden="true">{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span>
              </th>
            </tr>
            <tr class="subheader-row-2">
              <th v-for="(cell, i) in subHeaderCells" :key="i" v-bind="cell.attrs" v-html="cell.html"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, ri) in sortedRows" :key="ri" class="data-row">
              <td v-for="(col, ci) in flatColumns" :key="col.id" :class="[col.class, col.id==='name' ? 'name-cell' : '']" :style="getColStyle(ci)">
                <template v-if="col.id==='name'">
                  <div class="name-cell-wrapper">
                    <userAvatar
                      v-if="r.resolvedSalesAgentId"
                      :size="28"
                      layout="row"
                      :showSalesAgent="true"
                      :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                      :isSalesTeam="false"
                      :salesAgentId="r.resolvedSalesAgentId"
                    />
                    <div v-else class="agent-avatar fallback" :aria-label="'Initialen '+ r.initials">{{ r.initials }}</div>
                  </div>
                </template>
                <template v-else>
                  {{ formatCell(r[col.id], col) }}
                </template>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td :colspan="flatColumns.length" class="empty-message">Keine Daten gefunden.</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td v-for="col in flatColumns" :key="col.id" class="footer-cell">{{ formatCell(bottomRow[col.id], col) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { fetchFromBackend } from '@/stores/helpers/backend.mjs'
import Multiselect from 'vue-multiselect'
import userAvatar from '@/components/userAvatar.vue'
const userStore = useUserStore()

// State
// UI mode: 'datenbasiert' (quota/cumulative) or 'zeitbasiert' (date range)
const mode = ref('datenbasiert')
const filters = ref([
  { key: 'list', label: 'Liste', options: [], defaultValue: '', value: '' },
  { key: 'product', label: 'Produkt', options: [], defaultValue: '', value: '' },
  { key: 'salesTeam', label: 'Sales Team', options: [], defaultValue: '', value: '' },
  { key: 'salesAgent', label: 'Sales Agent', options: [], defaultValue: '', value: '' }
])
// Date range (modern filter addition for zeitbasiert mode)
const dateStart = ref('')
const dateEnd = ref('')
// Debounce handling
const autoRefreshEnabled = ref(true)
let debounceTimer = null
const DEBOUNCE_MS = 350
const rows = ref([])
const bottomRow = ref({})
const loading = ref(true)
const error = ref('')
const columnGroups = ref([])
const flatColumns = ref([])
const subHeaderCells = ref([])
const sortState = ref({ col: '', dir: 'asc' })
const tableRef = ref(null)
const scaleWrapperRef = ref(null)
const colWidths = ref([])
const tableScale = ref(1)
const tableWidth = ref(0)
// Debug state
const showDebug = ref(false)
const lastPayloadFlat = ref(null)
const lastPayloadNested = ref(null)
const lastResultSignature = ref('')
const resolvedIds = ref({ idCompany: -1, idCampaign: -1 })

function safeDiv(n, d) { return !d || isNaN(d) || d === 0 ? 0 : n / d }
function setMode(m){ mode.value = m }
function onZeitbasiertClick(){ if(mode.value!=='zeitbasiert'){ setMode('zeitbasiert'); /* keep date range filters active */ scheduleFetch() } }
function onDatenbasiertClick(){ if(mode.value!=='datenbasiert'){ setMode('datenbasiert'); /* clear date range for quota mode */ dateStart.value=''; dateEnd.value=''; scheduleFetch() } }
function onAktualisierenClick(){ fetchData() }
function reload() { fetchData() }
function resetFilters(){ filters.value.forEach(f=>f.value=f.defaultValue); dateStart.value=''; dateEnd.value=''; scheduleFetch() }
function applyFilters(){ scheduleFetch() }
function scheduleFetch(){
  if(!autoRefreshEnabled.value){ return }
  if(debounceTimer){ clearTimeout(debounceTimer) }
  debounceTimer = setTimeout(()=>{ fetchData() }, DEBOUNCE_MS)
}
function sortBy(colId) {
  if (sortState.value.col === colId) {
    sortState.value.dir = sortState.value.dir === 'asc' ? 'desc' : 'asc'
  } else {
    sortState.value.col = colId; sortState.value.dir = 'asc'
  }
}
function onHeaderClick(colId){ sortBy(colId) }
function normalizeForSort(val, colId){
  if(val == null || val === '') return null
  // percent & currency columns are still numeric already
  if(typeof val === 'number') return val
  // try numeric parsing
  const num = Number(val)
  if(!isNaN(num)) return num
  return String(val).toLowerCase()
}
const sortedRows = computed(() => {
  if (!sortState.value.col) return rows.value
  const colId = sortState.value.col
  const dir = sortState.value.dir === 'asc' ? 1 : -1
  return [...rows.value].sort((a,b) => {
    const A = normalizeForSort(a[colId], colId)
    const B = normalizeForSort(b[colId], colId)
    if(A === null && B === null) return 0
    if(A === null) return 1
    if(B === null) return -1
    if(typeof A === 'number' && typeof B === 'number') return (A - B) * dir
    return String(A).localeCompare(String(B)) * dir
  })
})

function getHeaderStyle(col) {
  if (col.id === 'unbearbeitet') return { background: '#D50000', color: '#fff' }
  if (['bearbeitet', 'durcharbeitung', 'bearbeitetNeu'].includes(col.id)) return { background: '#cccccc' }
  if (['Ges', 'OT'].includes(col.id)) return { background: '#9CCC65' }
  if (['beendetGes', 'beendetErf', 'anzGesch', 'unguel'].includes(col.id)) return { background: '#D50000', color: '#fff' }
  if (col.id === 'wvl') return { background: '#FEFE00' }
  const group = columnGroups.value.find(g => g.columns.includes(col)); return group ? { background: group.color } : {}
}
function breakHeader(label) { return label.replace(/( |\-)/g, '$1<br>') }
function formatCell(value, col) { if (value == null || value === '') return ''; if (typeof value === 'number' || (!isNaN(value) && value !== '')) { const r = Math.round(Number(value)); if (col.type === 'percent') return `${r}${col.unit || ''}`; if (col.type === 'currency') return `${r.toLocaleString('de-DE')} €`; return r.toLocaleString('de-DE') } return value }

function onAvatarError(e, row){
  if(e && e.target){ e.target.remove(); row.avatarUrl = null }
}

// Helper: normalize names (umlauts, spacing) for matching
function normalizeName(str){
  return (str||'')
    .toLowerCase()
    .replace(/ä/g,'ae')
    .replace(/ö/g,'oe')
    .replace(/ü/g,'ue')
    .replace(/ß/g,'ss')
    .replace(/\s+/g,' ') // collapse multiple spaces
    .trim()
}

function buildHeaders(currentMode) {
  // datenbasiert (quota) shows cumulative workload (Auslastung) + closing quotas
  if (currentMode === 'datenbasiert') {
    const groups = [
      { label: '', color: '#fff', columns: [
        { id: 'name', label: 'Name', class: 'sticky-col left-col', type: 'text' },
        { id: 'area', label: 'Area', class: 'sticky-col', type: 'text' }
      ]},
      { label: '', color: '#fff', columns: [
        { id: 'gesamt', label: 'Gesamt', class: 'right-align', type: 'count' },
        { id: 'unbearbeitet', label: 'un-bearbeitet', class: 'right-align', type: 'count' },
        { id: 'bearbeitet', label: 'bearbeitet', class: 'right-align', type: 'count' },
        { id: 'durcharbeitung', label: 'Durch-arbeitung', class: 'right-align', type: 'percent', unit: '%' }
      ]},
      { label: 'Entscheider Kontakt hergestellt', color: 'var(--band-blue)', columns: [
        { id: 'anzKonNeinGes', label: 'Anzahl Kontakt-versuche', class: 'right-align', type: 'count' },
        { id: 'entKonJaGes', label: 'Anzahl Kontakte', class: 'right-align', type: 'percent' },
        { id: 'entKonJaTel', label: 'Tele-fonate', class: 'right-align', type: 'count' },
        { id: 'entKonJaOrt', label: 'Besuche vor Ort', class: 'right-align', type: 'count' },
        { id: 'entKonShare', label: 'Entscheider Quote', class: 'right-align', type: 'percent', unit: '%' }
      ]},
      { label: 'Entscheider Termin', color: 'var(--band-beige)', columns: [
        { id: 'ausEntTerGes', label: 'Gesamt', class: 'right-align', type: 'count' },
        { id: 'ausEntTerTel', label: 'Telefon-termin', class: 'right-align', type: 'count' },
        { id: 'ausEntTerOrt', label: 'persönlicher Termin', class: 'right-align', type: 'count' },
        { id: 'entTerShare', label: 'Termin Quote', class: 'right-align', type: 'percent', unit: '%' }
      ]},
      { label: 'Angebote', color: 'var(--band-yellow)', columns: [
        { id: 'anzAngKw', label: 'Angebot in #', class: 'right-align', type: 'count' },
        { id: 'anzAngEu', label: 'Angebot in ', class: 'right-align', type: 'currency', unit: '€' },
        { id: 'anzAngDurch', label: 'Angebots-durchschnitt in ', class: 'right-align', type: 'currency', unit: '€' }
      ]},
      { label: 'Auftragsabschluss', color: 'var(--band-green)', columns: [
        { id: 'abschAngKw', label: 'Kunden in #', class: 'right-align', type: 'count' },
        { id: 'abschAngEu', label: 'Umsatz in ', class: 'right-align', type: 'currency', unit: '€' },
        { id: 'abschAngDurch', label: 'Auftrags-durchschnitt in ', class: 'right-align', type: 'currency', unit: '€' }
      ]},
      { label: 'Entscheider Termin', color: 'var(--band-beige)', columns: [
        { id: 'ausEntTerGes2', label: 'Gesamt', class: 'right-align', type: 'count' },
        { id: 'ausEntTerOrt2', label: 'pers. Termin', class: 'right-align', type: 'count' },
        { id: 'abschOT', label: 'OT mit Abschluss', class: 'right-align', type: 'count' },
        { id: 'Ges', label: 'Ges', class: 'right-align', type: 'percent', unit: '%' },
        { id: 'OT', label: 'OT', class: 'right-align', type: 'percent', unit: '%' }
      ]},
      { label: 'Nicht mehr in Bearbeitung', color: 'var(--band-red-strong)', columns: [
        { id: 'beendetGes', label: 'Gesamt', class: 'right-align red-text', type: 'count' },
        { id: 'beendetErf', label: 'erfolgreich beendet', class: 'right-align', type: 'count' },
        { id: 'anzGesch', label: 'davon gescheitert', class: 'right-align', type: 'count' },
        { id: 'unguel', label: 'Ungültig', class: 'right-align', type: 'count' }
      ]}
    ]
    const flat = groups.flatMap(g => g.columns)
    const cells = []
    for (let i = 0; i < flat.length; i++) {
      const id = flat[i].id
      if (id === 'anzKonNeinGes') { cells.push({ html: 'Nein', attrs: { class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, textAlign: 'center' } } }); continue }
      if (i === flat.findIndex(c => c.id === 'gesamt')) { cells.push({ html: 'Datensätze', attrs: { colspan: 4, class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, textAlign: 'center' } } }); i += 3; continue }
      if (i === flat.findIndex(c => c.id === 'entKonJaGes')) { cells.push({ html: 'Ja', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, textAlign: 'center' } } }); i += 2; continue }
      if (i === flat.findIndex(c => c.id === 'ausEntTerGes')) { cells.push({ html: 'In dem ausgewählten Zeitraum vereinbart<br>(Tätigkeit)', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, textAlign: 'center' } } }); i += 2; continue }
      if (i === flat.findIndex(c => c.id === 'ausEntTerGes2')) { cells.push({ html: 'Bis heute vereinbarte Termine<br>(Auslastung)', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, textAlign: 'center' } } }); i += 2; continue }
      if (i === flat.findIndex(c => c.id === 'Ges')) { cells.push({ html: 'Abschluss-<br>quote', attrs: { colspan: 2, class: 'additional-subheader', style: { background: '#9CCC65', textAlign: 'center' } } }); i += 1; continue }
      if (i === flat.findIndex(c => c.id === 'abschAngKw')) { cells.push({ html: 'resultierte Abschlüsse', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, textAlign: 'center' } } }); i += 2; continue }
      if (i === flat.findIndex(c => c.id === 'beendetGes')) { cells.push({ html: 'für die aktuelle Kampagne nicht mehr verwertbar', attrs: { colspan: 4, class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, color: '#fff', textAlign: 'center' } } }); i += 3; continue }
      cells.push({ html: '&nbsp;', attrs: { class: 'additional-subheader', style: { background: groups.find(g => g.columns.includes(flat[i]))?.color, color: ['beendetGes', 'beendetErf', 'anzGesch', 'unguel'].includes(id) ? '#fff' : undefined } } })
    }
    return { groups, flat, cells }
  }
  // zeitbasiert (dateRange) EXACT legacy columns from old file (no additions/removals)
  const groups = [
    { label: '', color: '#fff', columns: [
      { id: 'name', label: 'Name', class: 'sticky-col left-col', type: 'text' },
      { id: 'area', label: 'Area', class: 'sticky-col', type: 'text' }
    ]},
    { label: '', color: '#fff', columns: [
      { id: 'gesamt', label: 'Gesamt', class: 'right-align', type: 'count' },
      { id: 'unbearbeitet', label: 'un-bearbeitet', class: 'right-align', type: 'count' },
      { id: 'wvl', label: 'WVL >1 Tag', class: 'right-align', type: 'count' }
    ]},
    { label: 'Bearbeitung', color: '#cccccc', columns: [
      { id: 'bearbeitet', label: 'bearbeitet', class: 'right-align', type: 'count' },
      { id: 'bearbeitetNeu', label: 'davon neu', class: 'right-align', type: 'count' }
    ]},
    { label: 'Entscheider Kontakt hergestellt', color: 'var(--band-blue)', columns: [
      { id: 'anzKonNeinGes', label: 'Anzahl Kontakt-versuche', class: 'right-align', type: 'count' },
      { id: 'entKonJaGes', label: 'Anzahl Kontakte', class: 'right-align', type: 'percent' },
      { id: 'entKonJaTel', label: 'Tele-fonate', class: 'right-align', type: 'count' },
      { id: 'entKonJaOrt', label: 'Besuche vor Ort', class: 'right-align', type: 'count' }
    ]},
    { label: 'Entscheider Termin', color: 'var(--band-beige)', columns: [
      { id: 'tEntTerGes', label: 'Gesamt', class: 'right-align', type: 'count' },
      { id: 'tEntTerTel', label: 'Telefon-termin', class: 'right-align', type: 'count' },
      { id: 'tEntTerOrt', label: 'persönlicher Termin', class: 'right-align', type: 'count' },
      { id: 'ausEntTerGes', label: 'Gesamt', class: 'right-align', type: 'count' },
      { id: 'ausEntTerTel', label: 'Telefon-termin', class: 'right-align', type: 'count' },
      { id: 'ausEntTerOrt', label: 'persönlicher Termin', class: 'right-align', type: 'count' },
      { id: 'ausEntTerGesNextWeek', label: 'Gesamt', class: 'right-align', type: 'count' },
      { id: 'ausEntTerTelNextWeek', label: 'Telefon-termin', class: 'right-align', type: 'count' },
      { id: 'ausEntTerOrtNextWeek', label: 'persönlicher Termin', class: 'right-align', type: 'count' }
    ]},
    { label: 'Plan', color: 'var(--band-olive)', columns: [
      { id: 'planDataSalesEuro', label: 'Umsatz in', class: 'right-align', type: 'currency', unit: '€' }
    ]},
    { label: 'Auftrags-Forecast', color: 'var(--band-olive)', columns: [
      { id: 'forecastAbschAnz', label: 'Kunden in #', class: 'right-align', type: 'count' },
      { id: 'forecastAbschEu', label: 'Umsatz in', class: 'right-align', type: 'currency', unit: '€' },
      { id: 'forecastAbschAngDurch', label: 'Auftrags-durchschnitt OT', class: 'right-align', type: 'currency', unit: '€' }
    ]},
    { label: 'Auftragsabschluss', color: 'var(--band-green)', columns: [
      { id: 'abschAngKw', label: 'Kunden in #', class: 'right-align', type: 'count' },
      { id: 'abschAngEu', label: 'Umsatz in ', class: 'right-align', type: 'currency', unit: '€' },
      { id: 'abschAngDurch', label: 'Auftrags-durchschnitt in ', class: 'right-align', type: 'currency', unit: '€' },
      { id: 'abschNeuKw', label: 'Kunden in #', class: 'right-align', type: 'count' },
      { id: 'abschNeuEu', label: 'Umsatz in ', class: 'right-align', type: 'currency', unit: '€' },
      { id: 'abschNeuDurch', label: 'Auftrags-durchschnitt in', class: 'right-align', type: 'currency', unit: '€' }
    ]}
  ]
  const flat = groups.flatMap(g => g.columns)
  const cells = []
  for (let i = 0; i < flat.length; i++) {
    const col = flat[i]
    if (col.id === 'name') { cells.push({ html: '&nbsp;', attrs: { colspan: 5, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 4; continue }
    if (col.id === 'anzKonNeinGes') { cells.push({ html: 'Nein', attrs: { class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); continue }
    if (i === flat.findIndex(c=>c.id==='bearbeitet')) { cells.push({ html: 'Datensätze', attrs: { colspan: 2, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 1; continue }
    if (i === flat.findIndex(c=>c.id==='entKonJaGes')) { cells.push({ html: 'Ja', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 2; continue }
    if (i === flat.findIndex(c=>c.id==='tEntTerGes')) { cells.push({ html: 'In dem ausgewählten Zeitraum<br>vereinbart (Tätigkeit)', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 2; continue }
    if (i === flat.findIndex(c=>c.id==='ausEntTerGes')) { cells.push({ html: 'In dem ausgewählten Zeitraum<br>vereinbart (Auslastung)', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 2; continue }
    if (i === flat.findIndex(c=>c.id==='ausEntTerGesNextWeek')) { cells.push({ html: 'Für die Folge KW vereinbart<br>(Auslastung)', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 2; continue }
    if (i === flat.findIndex(c=>c.id==='forecastAbschAnz')) { cells.push({ html: 'Im ausgewähltem<br>Zeitraum', attrs: { colspan: 2, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 1; continue }
    if (col.id === 'forecastAbschAngDurch') { cells.push({ html: 'letzte 8 Monate', attrs: { class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); continue }
    if (i === flat.findIndex(c=>c.id==='abschAngKw')) { cells.push({ html: 'resultierte Abschlüsse gesamt', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 2; continue }
    if (i === flat.findIndex(c=>c.id==='abschNeuKw')) { cells.push({ html: 'resultierte Neukunden', attrs: { colspan: 3, class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color, textAlign:'center' } } }); i += 2; continue }
    cells.push({ html: '&nbsp;', attrs: { class: 'additional-subheader', style: { background: groups.find(g=>g.columns.includes(col))?.color } } })
  }
  return { groups, flat, cells }
}

async function fetchData() {
  try {
    loading.value = true; error.value = ''
    // Legacy payload structure (no nested filter.filter), ensure single-select values unwrap from vue-multiselect option objects
    const getFilterVal = (key) => {
      const f = filters.value.find(fl=>fl.key===key)
      if(!f) return ''
      if(f.value == null || f.value === '') return ''
      return typeof f.value === 'object' ? (f.value.value ?? '') : f.value
    }
    // Robust campaign/company fallback (old file used URL params). Use currentSelection first, then selectedCampaign getter, then first campaign.
    const idCompany = userStore.currentSelection?.campaign?.company?.id
      ?? userStore.selectedCampaign?.company?.id
      ?? userStore.campaigns?.[0]?.company?.id
      ?? -1
    const idCampaign = userStore.currentSelection?.campaign?.id
      ?? userStore.selectedCampaign?.id
      ?? userStore.campaigns?.[0]?.id
      ?? -1
    if(idCompany === -1 || idCampaign === -1){
      error.value = 'Keine Kampagne ausgewählt.'
      rows.value = []
      loading.value = false
      return
    }
    const baseFilter = {
      idCompany,
      idCampaign,
      idSalesAgents: String(getFilterVal('salesAgent')),
      idSalesTeams: String(getFilterVal('salesTeam')),
      idProduct: String(getFilterVal('product')),
      idList: String(getFilterVal('list')),
      week: -1,
      dateStart: mode.value==='zeitbasiert' && dateStart.value ? dateStart.value : '',
      dateEnd: mode.value==='zeitbasiert' && dateEnd.value ? dateEnd.value : ''
    }
    const viewMode = (mode.value==='datenbasiert') ? 'quota' : 'dateRange'
    // Try nested first (seems current backend may expect filter.filter) then flat fallback
    const nestedPayload = { id: userStore.id, filter: { filter: { ...baseFilter }, view: { mode: viewMode } }, view: { mode: viewMode } }
    const flatPayload = { id: userStore.id, filter: { ...baseFilter }, view: { mode: viewMode } }
    lastPayloadNested.value = nestedPayload
    lastPayloadFlat.value = flatPayload
    resolvedIds.value = { idCompany, idCampaign }
    let result = await fetchFromBackend('statistics/funnelEvaluation', nestedPayload)
    if(!result || !Array.isArray(result.data) || result.data.length===0){
      const fallback = await fetchFromBackend('statistics/funnelEvaluation', flatPayload)
      if(fallback && Array.isArray(fallback.data) && fallback.data.length){
        result = fallback
        lastResultSignature.value = 'Fallback(flat) genutzt'
      } else {
        lastResultSignature.value = 'Beide leer'
      }
    } else {
      lastResultSignature.value = 'Nested genutzt'
    }
    // Temporarily disable auto refresh while we mutate options (prevents watcher loop)
    autoRefreshEnabled.value = false
    if (result) {
      if (result.lists && filters.value[0]) filters.value[0].options = result.lists.map(l => ({ value: l.id, label: l.name }))
      if (result.products && filters.value[1]) filters.value[1].options = result.products.map(p => ({ value: p.id, label: p.name }))
      if (result.salesTeams && filters.value[2]) { const sorted = [...result.salesTeams].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })); filters.value[2].options = sorted.map(t => ({ value: t.id, label: t.name })) }
      if (result.salesAgents && filters.value[3]) filters.value[3].options = result.salesAgents.map(a => ({ value: a.id, label: `${a.firstname} ${a.surname}` }))
    }
    autoRefreshEnabled.value = true
    if (Array.isArray(result?.data)) {
      rows.value = result.data.map(agent => ({
      idSalesAgent: agent.id ?? agent.idSalesAgent ?? null,
      name: `${agent.firstname} ${agent.surname}`,
      area: agent.nameSalesTeam ?? '',
      gesamt: agent.anzPotGes ?? agent?.potential?.gesamt ?? 0,
      unbearbeitet: agent.anzUnbDs ?? agent?.potential?.unbearbeitet ?? 0,
      bearbeitet: agent.anzBearb ?? agent?.potential?.bearbeitent ?? 0,
      durcharbeitung: safeDiv(agent.anzBearb, agent.anzPotGes) * 100,
      anzKonNeinGes: agent.entKonNeinGes ?? agent?.kontakte?.entKonNeinGes ?? 0,
      entKonJaGes: agent.entKonJaGes ?? agent?.kontakte?.entKonJaGes ?? 0,
      entKonJaTel: agent.entKonJaTel ?? agent?.kontakte?.entKonJaTel ?? 0,
      entKonJaOrt: agent.entKonJaOrt ?? agent?.kontakte?.entKonJaOrt ?? 0,
      entKonShare: (agent.entQuot || 0) * 100,
      // appointments inside selected range (zeitbasiert/dateRange mode)
      ausEntTerGes: agent.ausEntTerGes ?? agent.tEntTerGes ?? 0,
      ausEntTerTel: agent.ausEntTerTel ?? agent.tEntTerTel ?? 0,
      ausEntTerOrt: agent.ausEntTerOrt ?? agent.tEntTerOrt ?? 0,
      entTerShare: (agent.terQuot || 0) * 100,
      anzAngKw: agent.anzAngKw ?? 0,
      anzAngEu: agent.anzAngEu ?? 0,
      anzAngDurch: agent.anzAngDurch ?? 0,
      abschAngKw: agent.abschAngKw ?? 0,
      abschAngEu: agent.abschAngEu ?? 0,
      abschAngDurch: agent.abschAngDurch ?? 0,
      // cumulative workload (datenbasiert/quota mode)
      ausEntTerGes2: agent.ausEntTerGes ?? 0,
      ausEntTerOrt2: agent.ausEntTerOrt ?? 0,
      abschOT: agent.abschAngFunnelAppointment ?? 0,
      Ges: (agent.abschQuot || 0) * 100,
      OT: (agent.abschQuotAppointment || 0) * 100,
      beendetGes: agent.beendetGes ?? 0,
  beendetErf: agent.beendetErf ?? 0,
  anzGesch: agent.anzGesch ?? 0,
  unguel: agent.unguel ?? 0,
      // legacy zeitbasiert-only fields
      wvl: agent.anzToDo ?? 0,
      bearbeitetNeu: agent.anzBearbNeu ?? 0,
      tEntTerGes: agent.tEntTerGes ?? 0,
      tEntTerTel: agent.tEntTerTel ?? 0,
      tEntTerOrt: agent.tEntTerOrt ?? 0,
      ausEntTerGesNextWeek: agent.ausEntTerGesNextWeek ?? 0,
      ausEntTerTelNextWeek: agent.ausEntTerTelNextWeek ?? 0,
      ausEntTerOrtNextWeek: agent.ausEntTerOrtNextWeek ?? 0,
      planDataSalesEuro: agent.planDataSalesEuro ?? 0,
      forecastAbschAnz: agent.forecastAbschAnz ?? 0,
      forecastAbschEu: agent.forecastAbschEu ?? 0,
      forecastAbschAngDurch: agent.forecastAbschAngDurch ?? 0,
      abschNeuKw: agent.abschNeuKw ?? 0,
      abschNeuEu: agent.abschNeuEu ?? 0,
      abschNeuDurch: agent.abschNeuDurch ?? 0,
      // avatar augmentation
      avatarUrl: (agent.id || agent.idSalesAgent) ? `/avatars/${agent.id || agent.idSalesAgent}.png` : null,
      initials: `${(agent.firstname||'').charAt(0)}${(agent.surname||'').charAt(0)}`.toUpperCase()
      })).map(r => ({ ...r, resolvedSalesAgentId: resolveSalesAgentId(r) }))
    } else {
      rows.value = []
    }
    const built = buildHeaders(mode.value)
    columnGroups.value = built.groups
    flatColumns.value = built.flat
    subHeaderCells.value = built.cells
    const sum = key => rows.value.reduce((a, r) => a + (Number(r[key]) || 0), 0)
    bottomRow.value = {}
    flatColumns.value.forEach(col => {
      if (col.id === 'name') bottomRow.value.name = 'Auswertung'
      else if (col.id === 'area') bottomRow.value.area = ''
      else if (col.id === 'durcharbeitung') bottomRow.value.durcharbeitung = safeDiv(sum('bearbeitet'), sum('gesamt')) * 100
      else if (col.id === 'entKonShare') bottomRow.value.entKonShare = safeDiv(sum('entKonJaGes'), sum('bearbeitet')) * 100
      else if (col.id === 'entTerShare') bottomRow.value.entTerShare = safeDiv(sum('ausEntTerOrt'), sum('entKonJaGes')) * 100
      else if (col.id === 'anzAngDurch') bottomRow.value.anzAngDurch = safeDiv(sum('anzAngEu'), sum('anzAngKw'))
      else if (col.id === 'abschAngDurch') bottomRow.value.abschAngDurch = safeDiv(sum('abschAngEu'), sum('abschAngKw'))
  else if (col.id === 'Ges') bottomRow.value.Ges = safeDiv(sum('abschAngKw'), sum('ausEntTerGes2')) * 100
  else if (col.id === 'OT') bottomRow.value.OT = safeDiv(sum('abschOT'), sum('ausEntTerOrt2')) * 100
      else if (col.id === 'forecastAbschAngDurch') bottomRow.value.forecastAbschAngDurch = safeDiv(sum('forecastAbschEu'), sum('forecastAbschAnz'))
      else if (col.id === 'abschNeuDurch') bottomRow.value.abschNeuDurch = safeDiv(sum('abschNeuEu'), sum('abschNeuKw'))
      else bottomRow.value[col.id] = sum(col.id)
    })
    if (!rows.value.length) error.value = 'Keine Daten gefunden.'
  } catch (err) { error.value = 'Fehler beim Laden der Daten.' }
  finally { loading.value = false; nextTick(() => { calculateColWidths(); rescaleTable() }) }
}

function calculateColWidths() {
  if (!flatColumns.value.length) return
  const maxWidths = flatColumns.value.map(() => 0)
  const sampleRows = rows.value.slice(0, 10)
  sampleRows.forEach(r => {
    flatColumns.value.forEach((col, i) => {
      const val = formatCell(r[col.id], col)
      const span = document.createElement('span'); span.style.visibility = 'hidden'; span.style.position = 'absolute'; span.style.whiteSpace = 'nowrap'; span.style.fontSize = '14px'; span.textContent = val; document.body.appendChild(span); const w = span.offsetWidth; document.body.removeChild(span); if (w > maxWidths[i]) maxWidths[i] = w
    })
  })
  flatColumns.value.forEach((col, i) => {
    breakHeader(col.label).split(/<br\s*\/?/gi).forEach(line => {
      const span = document.createElement('span'); span.style.visibility = 'hidden'; span.style.position = 'absolute'; span.style.whiteSpace = 'nowrap'; span.style.fontSize = '14px'; span.textContent = line.trim(); document.body.appendChild(span); const w = span.offsetWidth; document.body.removeChild(span); if (w > maxWidths[i]) maxWidths[i] = w
    })
  })
  const padded = maxWidths.map(w => Math.max(w + 12, 40))
  colWidths.value = padded
  tableWidth.value = padded.reduce((a, b) => a + b, 0)
}
function rescaleTable() { nextTick(() => { if (!tableRef.value || !scaleWrapperRef.value) return; const w = tableRef.value.offsetWidth; const wrap = scaleWrapperRef.value.offsetWidth; tableScale.value = (w > 0 && wrap > 0) ? Math.min(1, wrap / w) * 0.95 : 0.95 }) }
function getColStyle(i) { if (!colWidths.value[i]) return { width: 'auto' }; return { width: colWidths.value[i] + 'px', minWidth: colWidths.value[i] + 'px' } }

onMounted(() => { fetchData() })
// Auto-refresh when date range changes in zeitbasiert mode (debounced)
watch([dateStart, dateEnd], () => { if(mode.value==='zeitbasiert' && dateStart.value && dateEnd.value){ scheduleFetch() } })
// Watch only the selected values (not options) to avoid loops
watch(() => filters.value.map(f => f.value), () => { scheduleFetch() })
watch(rows, () => nextTick(() => calculateColWidths()))
watch(colWidths, () => rescaleTable())
// Re-fetch once campaign selection becomes available
watch(() => userStore.currentSelection?.campaign?.id, (newId, oldId) => {
  if(newId && newId !== oldId){ scheduleFetch() }
})
watch(() => userStore.currentSelection?.campaign?.company?.id, (newId, oldId) => {
  if(newId && newId !== oldId){ scheduleFetch() }
})

// --- Performance enhancement: precomputed colleagueMap + overridden resolveSalesAgentId ---
const colleagueMap = computed(() => {
  const map = new Map()
  if(!userStore.colleagues) return map
  for(const wrap of userStore.colleagues){
    const c = wrap?.value || wrap
    if(!c) continue
    const norm = normalizeName(`${c.firstName||''} ${c.lastName||''}`)
    if(norm){
      const id = c.isSalesAgent?.id || c.id
      if(id) map.set(norm, id)
    }
  }
  return map
})
function resolveSalesAgentId(row){
  if(row.idSalesAgent) return row.idSalesAgent
  const target = normalizeName(row.name)
  if(!target) return null
  return colleagueMap.value.get(target) || null
}
</script>

<style scoped>
/* Force all containers and table to fit screen and shrink font */
.funnel-table-container,
.funnel-table-scroll,
.responsive-table {
  width: 100% !important;
  max-width: 100vw !important;
  min-width: 0 !important;
  box-sizing: border-box;
}

.responsive-table,
.responsive-table th,
.responsive-table td {
  /* font-size removed */
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  padding: 0.01em 0.05em;
  min-width: 0;
  max-width: none;
}

/* Rotate header text to save horizontal space */
/* Sticky header and subheaders */
/* Sticky header and subheaders with stacking */
.responsive-table th {
  writing-mode: initial;
  transform: none;
  text-align: left;
  vertical-align: bottom;
  height: auto;
  max-width: none;
  padding: 0.05em 0.1em;
}

.responsive-table .main-header-row th {
  position: sticky;
  top: 0;
  z-index: 12;
  background: #fff;
}

.responsive-table .subheader-row-1 th {
  position: sticky;
  top: 38px;
  /* Adjust to header height */
  z-index: 11;
  background: #f9f9f9;
}

.responsive-table .subheader-row-2 th {
  position: sticky;
  top: 110px;
  /* Adjust to header + subheader height */
  z-index: 10;
  background: #f3f3f3;
}

/* Responsive table: shrink columns and wrap text as needed */
.responsive-table {
  width: 100%;
  table-layout: fixed;
}

.responsive-table th,
.responsive-table td {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  padding: 0.01em 0.05em;
  min-width: 0;
  max-width: none;
}

@media (max-width: 900px) {

  .responsive-table th:nth-child(n+6),
  .responsive-table td:nth-child(n+6) {
    display: table-cell;
    /* Revert column hiding on small screens */
  }
}

/* Sticky footer row for the table */
.sticky-footer-row {
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.sticky-footer-cell {
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-shadow: 0 -2px 8px 0 rgba(0, 0, 0, 0.04);
}

/* Sticky footer row for the table */
tfoot {
  position: sticky;
  bottom: 0;
  z-index: 10;
}

tfoot td {
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-shadow: 0 -2px 8px -4px rgba(0, 0, 0, 0.04);
}

.funnel-table-scroll {
  overflow-y: auto;
  max-height: 400px;
}

.funnel-table-scale-wrapper {
  width: 100vw;
  max-width: 100vw;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: 0;
  /* Center the scaled table */
  display: flex;
  justify-content: left;
  align-items: flex-start;
}

.funnel-table {
  transform-origin: left top;
  transition: transform 0.2s;
}

/* Name column auto-size */
.funnel-table .name-col {
  width: auto !important;
  min-width: 80px;
  /* max-width removed to allow JS to control width */
}

.funnel-table td:first-child,
.funnel-table th:first-child {
  white-space: nowrap;
  font-weight: 500;
  word-break: break-word;
  width: auto !important;
  min-width: 80px;
  /* max-width removed to allow JS to control width */
}

@media (max-width: 900px) {

  .funnel-table td:first-child,
  .funnel-table th:first-child {
    min-width: 60px;
    /* max-width removed to allow JS to control width */
  }
}

@media (max-width: 600px) {

  .funnel-table td:first-child,
  .funnel-table th:first-child {
    min-width: 40px;
    /* max-width removed to allow JS to control width */
  }
}

.group-header[data-band="red"] {
  background: var(--band-red);
}

.group-header[data-band="blue"] {
  background: var(--band-blue);
}

.group-header[data-band="beige"] {
  background: var(--band-beige);
}

.group-header[data-band="yellow"] {
  background: var(--band-yellow);
}

.group-header[data-band="green"] {
  background: var(--band-green);
}

.group-header[data-band="olive"] {
  background: var(--band-olive);
}

.group-header[data-band="orange"] {
  background: var(--band-orange);
}

.group-header[data-band="red-strong"] {
  background: var(--band-red-strong);
  color: #fff;
}

/* Color design tokens for table bands */
:global(html) {
  --table-bg: #ffffff;
  --table-grid: #e6e6e6;
  --text: #1f2937;
  --muted: #6b7280;

  --band-red: #d32f2f;
  --band-blue: #B3E5FC;
  --band-beige: #FDE1B1;
  --band-yellow: #FEFE00;
  --band-green: #9CCC65;
  --band-olive: #c5d86d;
  --band-orange: #ffcc80;
  --band-red-strong: #D50000;

  --total-row-bg: #fff7c2;
  --sticky-shadow: 8px;
}

.funnel-table-container {
  width: 100%;
  max-width: 100%;
  margin-top: calc(12px * 8);
  padding: 0 !important;
  display: block;
  overflow-x: hidden;
  box-sizing: border-box;
}

.filter-bar {
  width: 96vw;
  max-width: 96vw;
  display: flex;
  gap: 12px;
  padding: 12px 0 8px 0;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  position: relative;
  z-index: 10;
}

/* Table container and table fill the viewport width */
/* Center the table horizontally in the scroll area */
/* Center the table horizontally in the scroll area, always */
.funnel-table-responsive {
  /* width and max-width removed to allow inline width to control */
  overflow-x: hidden;
  box-sizing: border-box;
  padding: 0 8px;
}

.funnel-table {
  border-collapse: collapse;
  background: #fff;
  color: #000;
  overflow-x: hidden;
  /* width, max-width, and table-layout removed to allow inline width and table-layout: fixed to control */
}

.funnel-table th,
.funnel-table td {
  border: 1px solid #E0E0E0;
  border-width: 1px;
  padding: 6px 4px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #fff;
  color: #000;
  line-height: 1.2;
}

/* Name column auto-size */
.funnel-table td:first-child,
.funnel-table th:first-child {
  font-weight: 500;
  white-space: nowrap;
  word-break: break-word;
}

@media (max-width: 900px) {

  .funnel-table td:first-child,
  .funnel-table th:first-child {
    /* reduced on small screens */
    font-size: 0.9rem;
  }
}

@media (max-width: 600px) {

  .funnel-table td:first-child,
  .funnel-table th:first-child {
    font-size: 0.8rem;
  }
}

@media (max-width: 900px) {

  .funnel-table th,
  .funnel-table td {
    padding: 6px;
  }
}

@media (max-width: 600px) {

  .funnel-table th,
  .funnel-table td {
    padding: 4px;
  }
}

.funnel-table-scroll {
  max-height: 80vh;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
  overflow-anchor: none;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  display: block;
  box-sizing: border-box;
}

.funnel-table-footer-sticky {
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 100;
  box-shadow: 0 -4px 8px -4px var(--table-grid, #e6e6e6);
  overflow: hidden;
  background: none;
  /* width removed to allow inline width to control */
}

.funnel-table-footer-sticky .funnel-table {
  table-layout: fixed;
  margin: 0;
  /* width removed to allow inline width to control */
}

.funnel-table tfoot td {
  position: sticky;
  bottom: 0;
  background: var(--total-row-bg, #fff7c2);
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 -4px 8px -4px var(--table-grid, #e6e6e6);
}

@media (max-width: 900px) {
  .funnel-table {
    width: 100vw;
    max-width: 100vw;
    border-collapse: collapse;
    table-layout: auto;
    background: #fff;
    color: #000;
    overflow-x: hidden;
    margin-left: 0 !important;
    padding-left: 0 !important;
    box-sizing: border-box;
    padding: 4px;
  }

}

.funnel-table tfoot td {
  position: sticky;
  bottom: 0;
  background: var(--total-row-bg, #fff7c2);
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 -4px 8px -4px var(--table-grid, #e6e6e6);
}

.empty-message {
  padding: 16px;
  text-align: center;
  color: #888;
}

.error-banner {
  background: #fee2e2;
  color: #b91c1c;
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 6px;
  text-align: center;
}

.skeleton-table {
  width: 100%;
  padding: 16px 0;
}

.skeleton-row {
  display: flex;
  gap: 0;
  margin-bottom: 4px;
}

.skeleton-cell {
  flex: 1;
  height: 28px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  border-radius: 4px;
  margin-right: 2px;
}

/* Sticky left columns */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fff;
  box-shadow: 8px 0 8px -8px var(--table-grid, #e6e6e6);
}

.left-col {
  left: 0;
}

.group-header {
  color: #111;
  font-weight: bold;
  text-align: center;
  border-bottom: 1px solid #ccc;
  background: #888;
  /* fallback for browsers not supporting CSS vars */
}
/* Modernized header visuals (retain original color via inline style) */
.group-header span { display:inline-block; padding:6px 4px; font-size:.72rem; letter-spacing:.5px; text-transform:uppercase; font-weight:600; }
.main-header-row .group-header { position:relative; overflow:hidden; }
.main-header-row .group-header::after { content:""; position:absolute; inset:0; background:linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0)); pointer-events:none; }
.subheader-row-1 th { position:relative; font-size:.7rem; font-weight:600; text-transform:uppercase; letter-spacing:.4px; }
.subheader-row-1 th.sortable-header { cursor:pointer; user-select:none; }
.subheader-row-1 th.sortable-header:focus { outline:2px solid #2563eb; outline-offset:2px; }
.subheader-row-1 th .sort-btn { background:transparent; border:none; cursor:pointer; font-size:.65rem; opacity:.6; margin-left:4px; }
.subheader-row-1 th .sort-btn:hover { opacity:1; }
.sort-indicator { margin-left:4px; font-size:.55rem; opacity:.7; }
.subheader-row-2 th { font-size:.6rem; font-weight:500; letter-spacing:.3px; }
.sorted-col { background-image:linear-gradient(to bottom, rgba(0,0,0,0.06), rgba(0,0,0,0.02)); box-shadow:inset 0 -2px 0 #0097a7; }

/* Name cell avatar layout */
.name-cell-wrapper { display:flex; align-items:center; gap:8px; justify-content:flex-start; }
.agent-avatar { width:28px; height:28px; border-radius:50%; object-fit:cover; box-shadow:0 2px 4px rgba(0,0,0,.15); border:2px solid #fff; }
.agent-avatar.fallback { display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#26A69A,#0097a7); color:#fff; font-size:.65rem; font-weight:600; text-transform:uppercase; }
.agent-name-text { font-weight:600; font-size:.75rem; white-space:nowrap; }

@media (max-width:900px){
  .agent-name-text { font-size:.7rem; }
  .agent-avatar { width:24px; height:24px; }
}
@media (max-width:600px){
  .agent-name-text { font-size:.65rem; }
  .agent-avatar { width:22px; height:22px; }
}

.right-align {
  text-align: right;
}

.bold {
  font-weight: bold;
}

.red-text {
  color: var(--band-red-strong, #c62828);
}

.footer-cell {
  background: var(--total-row-bg, #fff7c2);
  font-weight: bold;
}

.data-row:hover td {
  background: #f3f4f6;
}

.funnel-table td:focus {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
  z-index: 4;
}

.visually-hidden {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}


.toggle-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.toggle-btn {
  padding: 8px 20px;
  border-radius: 6px;
  border: 1.5px solid #0097a7;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.toggle-bar .toggle-btn.white {
  background: #fff !important;
  color: #111 !important;
}

.toggle-bar .toggle-btn.petrol {
  background: #26A69A !important;
  color: #111 !important;
}

/* ============================= */
/* Modern Filter Panel Redesign  */
/* ============================= */
.filters-panel {
  width: calc(100% - 48px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 18px 10px 18px;
  background: linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(245,247,250,0.85) 60%);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(200,210,220,0.6);
  border-radius: 18px;
  position: relative;
  margin: 4px 24px 12px 24px;
  box-shadow: 0 6px 14px -6px rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.08);
  z-index: 998;
}
.filters-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 18px;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 40%);
}
.panel-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}
.primary-row { justify-content: space-between; }
.filters-row { justify-content: flex-start; }
.branding { display:flex; flex-direction:column; gap:2px; }
.branding h2 { margin:0; font-size:1.25rem; font-weight:600; letter-spacing:.5px; }
.branding .subtitle { margin:0; font-size:.75rem; font-weight:500; color:#555; text-transform:uppercase; letter-spacing:1px; }

/* Mode segmented switch */
.mode-switch { display:flex; background:rgba(0,0,0,0.06); padding:4px; border-radius:14px; gap:4px; position:relative; }
.mode-switch .segment {
  position:relative;
  background:transparent;
  border:none;
  padding:8px 18px;
  font-weight:600;
  font-size:.85rem;
  border-radius:10px;
  cursor:pointer;
  color:#333;
  transition: all .25s ease;
}
.mode-switch .segment.active {
  background: linear-gradient(135deg,#26A69A,#0097a7);
  color:#fff;
  box-shadow:0 3px 8px -3px rgba(0,0,0,0.35);
}
.mode-switch .segment:not(.active):hover { background:rgba(255,255,255,0.7); }

/* Filter items */
.filter-item { min-width:160px; flex:1 1 180px; display:flex; flex-direction:column; gap:6px; }
.filter-item.wide { flex:2 1 320px; }
.filter-label { font-size:.65rem; font-weight:600; text-transform:uppercase; letter-spacing:.5px; color:#555; }

/* vue-multiselect overrides */
.filter-select ::v-deep(.multiselect) { min-height:38px; }
.filter-select ::v-deep(.multiselect__tags) { border:1px solid #cdd5dd; border-radius:10px; background:#fff; padding:6px 10px; font-size:.8rem; }
.filter-select ::v-deep(.multiselect__single) { font-size:.8rem; }
.filter-select ::v-deep(.multiselect__input) { font-size:.8rem; }
.filter-select ::v-deep(.multiselect__option--highlight) { background:#26A69A; }
.filter-select ::v-deep(.multiselect__option--selected) { background:#0097a7; color:#fff; }

/* Date range group */
.date-range-group { display:flex; flex-direction:column; }
.date-range { display:flex; align-items:center; gap:8px; padding:6px 10px; border:1px solid #cdd5dd; background:#fff; border-radius:12px; }
.date-range input { border:none; background:transparent; font-size:.8rem; padding:4px 2px; outline:none; }
.date-range input::-webkit-calendar-picker-indicator { filter:invert(40%); cursor:pointer; }
.date-separator { opacity:.6; font-size:.9rem; }

/* Loading overlay spinner integration */
.loading-overlay.container { position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; width:100%; min-height:180px; background:linear-gradient(135deg, rgba(255,255,255,.9), rgba(245,247,250,.9)); border:1px solid #e2e8f0; border-radius:16px; }
.loading-overlay .loadingDescription { font-size:.8rem; font-weight:600; letter-spacing:.5px; color:#444; }
.loading-overlay .loader.fullPage { position:relative; width:64px; height:64px; min-width:64px; min-height:64px; }
/* Fade-in animation for loader */
.fade-in { animation: fadeIn .35s ease; }
@keyframes fadeIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Panel action buttons */
.panel-actions { display:flex; gap:10px; align-items:center; }
.btn { position:relative; border:none; cursor:pointer; font-weight:600; font-size:.75rem; letter-spacing:.5px; padding:8px 16px; border-radius:10px; transition: all .25s ease; }
.btn.primary { background:linear-gradient(135deg,#26A69A,#0097a7); color:#fff; }
.btn.primary:hover { filter:brightness(1.05); }
.btn.outline { background:#fff; border:1px solid #0097a7; color:#006a78; }
.btn.outline:hover { background:#e0f7f5; }
.btn.ghost { background:transparent; color:#555; }
.btn.ghost:hover { background:rgba(0,0,0,0.06); }
.btn:active { transform:translateY(1px); }

/* Debug box */
.debug-box { margin:8px 24px 0 24px; padding:8px 12px; background:#1e293b; color:#eee; border-radius:10px; font-size:.65rem; line-height:1.3; }
.debug-box details { cursor:pointer; }
.debug-pre { max-height:260px; overflow:auto; white-space:pre; font-family:monospace; }

@media (max-width:880px){
  .primary-row { flex-direction:column; align-items:stretch; }
  .panel-actions { justify-content:flex-end; }
  .branding h2 { font-size:1.05rem; }
}
@media (max-width:600px){
  .filters-panel { padding:12px 12px 8px 12px; border-radius:14px; }
  .mode-switch { width:100%; justify-content:space-between; }
  .filter-item { flex:1 1 140px; }
  .panel-actions { flex-wrap:wrap; }
  .btn { padding:6px 12px; }
}
</style>
